/**
 * Wire types for the users feature (PX-W6.2) — the `MongoUser` /
 * `MongoInviteToken` / `ProjectMember` / `MongoActivity` JSON the px backend
 * serialises. Everything the UI never reads or sends (credentials, tokens,
 * sessions, `agentServiceFor`) is deliberately absent from these shapes.
 *
 * INV-1/INV-8: the request bodies this module declares carry no
 * `organisations` array, no credential fields and no `members[]`/ACL arrays —
 * roster and access changes go through their dedicated endpoints only.
 */

/** One org membership on a user row (`MongoUser.organisations[]`). */
export interface ApiUserOrganisation {
  orgId: string
  orgName?: string
  role: string
  joinedAt?: string
}

/** A user row (`GET /{orgId}/users`, `GET /{orgId}/users/:id`). */
export interface ApiUser {
  /** The legacy slug — the external id every URL uses. */
  id: string
  /** The canonical ObjectId hex; identity comparisons against ACL stamps use this. */
  idHex?: string
  createdAt?: number
  name?: string | null
  email: string
  avatar?: string | null
  /** Platform job title (`MongoUser.role`). */
  role?: string | null
  /** Display role enum (`MongoUser.memberRole`). */
  memberRole?: string | null
  memberGroup?: string | null
  /** `ACTIVE` | `INACTIVE` | `INVITED` as the server wrote it. */
  memberStatus?: string | null
  lastActiveAt?: string | null
  joinedAt?: string | null
  invitedBy?: string | null
  deactivated?: boolean
  isGuest?: boolean | null
  organisations?: ApiUserOrganisation[]
}

/** A pending invite row (`GET /{orgId}/users/invites` — `token` never arrives). */
export interface ApiInvite {
  id: string
  email: string
  role?: string
  orgName?: string
  inviterName?: string
  projectId?: string
  projectName?: string
  clientId?: string
  projectAccessLevel?: string
  isExistingUser?: boolean
  /** The language the invitation e-mail was written in (a resend reuses it). */
  locale?: InviteLocale
  createdAt?: number | string
  expiresAt?: number | string
  acceptedAt?: number | string
}

/** The two e-mail languages an invite may carry. */
export type InviteLocale = 'en' | 'nl'

/** Every invite e-mail language, in picker order. */
export const INVITE_LOCALES: readonly InviteLocale[] = ['en', 'nl']

/** The invite language that matches a UI language: Dutch for `nl`, English otherwise. */
export const toInviteLocale = (language: string | undefined): InviteLocale =>
  language === 'nl' ? 'nl' : 'en'

/** Access levels a project invite may grant (OWNER is never grantable by invitation). */
export type InviteAccessLevel = 'READ' | 'WRITE'

/** Org roles an admin may assign. */
export type AssignableOrgRole = 'admin' | 'member' | 'viewer'

/** Every assignable org role, in picker order (`owner` is never among them). */
export const ASSIGNABLE_ORG_ROLES: readonly AssignableOrgRole[] = [
  'admin',
  'member',
  'viewer',
]

/** Every org role the role endpoints accept (including the non-invitable owner). */
export type OrgRole = AssignableOrgRole | 'owner'

/** Org roles an invite may carry: `owner` only when the caller is an owner (INV-I10, server-enforced). */
export type InvitableOrgRole = OrgRole

/** `POST /{orgId}/users/invite` body — exactly these fields, nothing else. */
export interface InviteInput {
  email: string
  role: InvitableOrgRole
  clientId?: string
  locale?: InviteLocale
}

/** `POST /{orgId}/projects/:projectId/members/invite` body. */
export interface ProjectInviteInput {
  email: string
  role: AssignableOrgRole
  accessLevel: InviteAccessLevel
  locale?: InviteLocale
}

/** `PUT /{orgId}/users/:id` body — display fields only. */
export type UserUpdateInput = Pick<ApiUser, 'name' | 'email' | 'avatar' | 'role'>

/** A project member row (`GET /{orgId}/projects/:projectId/members`). */
export interface MemberRow {
  id?: string
  /** ObjectId hex — matches `ApiUser.idHex`, not the slug. */
  userId?: string
  name?: string
  initials?: string
  role?: string
  /** `READ` | `WRITE` | `OWNER` as the server wrote it. */
  accessLevel?: string
  addedAt?: number
  addedBy?: string
  /** Absent means FIRM (mirrors `ProjectMember.getSideOrFirm`). */
  side?: 'FIRM' | 'CLIENT'
}

/** The project summary the members browser's selector reads (`GET /projects`). */
export interface ApiProjectSummary {
  id: string
  name?: string
  status?: string
}

/** An activity row (`GET /{orgId}/users/:userId/activity`) — rendered defensively. */
export interface ApiActivityEntry {
  id?: string
  content?: string
  type?: string
  actor?: string
  createdAtMillis?: number
  createdAt?: number
}
